import type { BaseQueryApi, BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";

import type { RootState, ExtraType } from "../../app/configureStore";
import { getTenant } from "../../helpers";

import { loggedOut, tokenReceived, userSelector } from "./authenticationSlice";
import { artesianSelector, curveExcelBlobUrlSelector } from "./envSlice";

type ServiceType = "artesianApi" | "azureBlobStorage";
export function ArkBaseQuery(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: ExtraType,
  service: ServiceType,
) {
  return ArkQuery(service)(args, api, extraOptions);
}

function getBaseUrl(service: ServiceType, api: BaseQueryApi) {
  switch (service) {
    case "artesianApi":
      return artesianSelector(api.getState() as RootState);
    case "azureBlobStorage":
      return curveExcelBlobUrlSelector(api.getState() as RootState);
    default:
      return "";
  }
}

function createBaseQuery(api: BaseQueryApi, service: ServiceType) {
  const url = getBaseUrl(service, api);
  const user = userSelector(api.getState() as RootState);
  const tenant = getTenant(user);
  const baseUrl = service === "azureBlobStorage" ? url : `${url}/${tenant}`;
  console.log("Base URL:", baseUrl);
  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers, { extra, arg }) => {
      if (arg && typeof arg === "object" && "body" in arg && arg.body instanceof FormData) {
        // Remove Content-Type for FormData to let the browser set it
        headers.delete("Content-Type");
      } else {
        // Set Content-Type to application/json for all other requests
        headers.set("Content-Type", "application/json");
        headers.set("Accept", "application/json");
      }
      if (service === "azureBlobStorage") {
        return headers;
      }
      let token = await (extra as ExtraType).authProvider.getToken(baseUrl);

      if (!token || token === "") {
        token = await (extra as ExtraType).authProvider.getToken(baseUrl);
        if (token) api.dispatch(tokenReceived(token));
        else api.dispatch(loggedOut());
      }

      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      return headers;
    },
  });
}

export function ArkQuery(service: ServiceType) {
  const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, ExtraType> = async (
    args,
    api,
    extraOptions,
  ) => {
    const baseQuery = createBaseQuery(api, service);
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
      result = await baseQuery(args, api, extraOptions);
    }
    return result;
  };
  return baseQuery;
}
